import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { formatDateToISODate } from "@/utils/dates";
import { Platform } from "@/models";
import { ReservationRow } from "@/pages/reservations";
import { apiUrl } from "@/layouts/apiUrl";
// Define enums for the fixed values
export enum PaymentMethod {
    PaymentMethodLocalPay = "Λογαριασμός Πληρωμών Ημεδαπής",
    PaymentMethodForeign = "Λογαριασμός Πληρωμών Αλλοδαπής",
    PaymentMethodCash = "Μετρητά",
    PaymentMethodPaymentOther = "Λοιποί",
}

export enum StrPlatform {
    Airbnb = "Airbnb",
    Booking = "Booking.com",
    Clickstay = "Clickstay",
    Homestay = "Homestay",
    LuxuryRetreats = "Luxury Retreats",
    OnlyApartments = "Only-apartments",
    TripAdvisor = "TripAdvisor",
    Other = "λλες ψηφιακές πλατφόρμες",
}

// Define the Reservation type
export interface StrReservation {
    amountReceived?: number;
    apartmentId: number;
    cancellationAmount?: number;
    cancellationDate?: string;
    endDate: string;
    guestId?: string;
    guestName?: string;
    guestTaxId?: string;
    isForeigner: boolean;
    paymentMethod: PaymentMethod;
    platform: StrPlatform;
    reservationId: string;
    startDate: string;
}

// Define the UploadTaskRequest type
export interface UploadTaskRequest {
    draftUpload?: boolean;
    reservations: StrReservation[];
}

// Define the UploadTaskResponse type
export interface UploadTaskResponse {
    taskId: number;
    requestId: string;
}

// Define the TaskStatusResponse type
export interface TaskStatusResponse {
    requestId: string;
    strUploadStatus: StrUploadStatus;
}

export interface StrUploadStatus {
    taskId: number;
    reservationUploadStatus: ReservationUploadStatus[];
}

// Define the ReservationUploadStatus type
export interface ReservationUploadStatus {
    reservationId: string;
    status: string;
    details: string;
    lastUpdated: string;
    serialNumberDeclaration: string;
}

function getPlatformEnumValue(platform: number): StrPlatform {
    switch (platform) {
        case Platform.Airbnb:
            return StrPlatform.Airbnb;
        case Platform.Booking:
            return StrPlatform.Booking;
        default:
            return StrPlatform.Other;
    }
}

/** STR rejects a whole batch if any reservation in it has no registry property. */
export function isFileableToStr(reservation: Pick<ReservationRow, "strApartmentID">): boolean {
    return reservation.strApartmentID > 0;
}

export function convertToReservation(reservation: ReservationRow): StrReservation {
    const income = reservation.receivableIncome - reservation.temporaryStayDuty - reservation.sustainabilityFee - reservation.vat;
    const strReservation = {
        reservationId: `${reservation.reservationCode}-${reservation.id}`,
        apartmentId: reservation.strApartmentID,
        startDate: formatDateToISODate(reservation.startDate),
        endDate: formatDateToISODate(reservation.endDate),
        isForeigner: true,
        platform: getPlatformEnumValue(reservation.platformEnum),
        paymentMethod: PaymentMethod.PaymentMethodForeign,
        guestId: reservation.reservationCode,
        guestName: reservation.guestName,
    };

    // Cancellation is what `isCancelled` says it is. It used to key off
    // `actualDaysOfStay === 0` — a proxy that agrees only because normalizeCancelled
    // zeroes a cancellation's nights. A cancelled row whose nights survived was
    // declared as a normal stay (cancellation never recorded), and a live zero-night
    // row was declared cancelled (income withheld).
    if (reservation.isCancelled) {
        const cancellationDate = new Date(reservation.startDate.getTime());
        cancellationDate.setDate(cancellationDate.getDate() - 1);
        return {
            cancellationAmount: income,
            cancellationDate: formatDateToISODate(cancellationDate),
            ...strReservation,
        };
    } else {
        return {
            amountReceived: income,
            ...strReservation,
        };
    }
}

export const strApi = createApi({
    reducerPath: "strApi",
    baseQuery: fetchBaseQuery({
        baseUrl: `${apiUrl}/api/admin/`,
        credentials: "include",
    }),
    endpoints: (builder) => ({
        uploadTask: builder.mutation<UploadTaskResponse, UploadTaskRequest>({
            query: (request) => ({
                url: `str/task`,
                method: "POST",
                body: request,
            }),
        }),

        getTaskStatus: builder.query<TaskStatusResponse, string>({
            query: (taskId) => ({
                url: `str/task/${taskId}`,
                method: "GET",
            }),
        }),
    }),
});

// Export the automatically generated hooks
export const { useUploadTaskMutation, useGetTaskStatusQuery } = strApi;
